import { env } from 'core/utils';
import mitt from 'mitt';
import { injectable } from 'inversify';
import { combineUrls } from './utils';
import type { BaseDocumentChangeModel } from './models';

const EVENTS_LIST = ['open', 'docUpdated', 'userLogout', 'accessRevoked', 'error', 'ping'] as const;

type Events = {
  docUpdated: BaseDocumentChangeModel;
  open: null;
  userLogout: null;
  accessRevoked: null;
  error: string;
  ping: null;
};

type EventNames = (typeof EVENTS_LIST)[number];

type Handler<T extends EventNames> = (e: Events[T]) => void;

@injectable()
export class BaseSseRepository {
  private sse: EventSource;

  private readonly baseUrl: string;

  private readonly events = mitt<Events>();

  constructor(url: string) {
    this.baseUrl = combineUrls(env.API_URL, url);
  }

  create(id: string) {
    const url = combineUrls(this.baseUrl, id, '/changes/events');

    this.sse = new EventSource(url, {
      withCredentials: true,
    });

    this.subscribe();

    return this;
  }

  destroy() {
    this.off('*');
    this.sse.close();
  }

  private onMessage = (e: MessageEvent<string>) => {
    this.events.emit(e.type as EventNames, e.data ? JSON.parse(e.data) : null);
  };

  private subscribe() {
    EVENTS_LIST.forEach(event => {
      this.sse.addEventListener(event, this.onMessage);
    });
  }

  private unsubscribe() {
    EVENTS_LIST.forEach(event => {
      this.sse.removeEventListener(event, this.onMessage);
    });
  }

  on<TEvent extends EventNames>(eventName: TEvent, handler: Handler<TEvent>) {
    this.events.on(eventName, handler);
  }

  off<TEvent extends EventNames>(eventName: TEvent | '*', handler?: Handler<TEvent>) {
    if (eventName === '*') {
      this.events.all.clear();
    } else {
      this.events.off(eventName, handler);
    }
  }
}
